import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";

const isProductionTracking = import.meta.env.VITE_IS_STAGING === "false";

const META_PIXEL_ID = "1072383254829696";

const sha256LowerTrim = (s) =>
  CryptoJS.SHA256(String(s).trim().toLowerCase()).toString(CryptoJS.enc.Hex);

const normalizePhone = (phone) => String(phone).replace(/\D+/g, "");

// Meta city normalization: lowercase, no spaces or punctuation.
const normalizeCity = (city) =>
  String(city)
    .trim()
    .toLowerCase()
    .replace(/[\s\p{P}\p{S}]+/gu, "");

/**
 * event_id shared by the browser CompleteRegistration and its server copy
 * (registerAdvertiserUser / registerAppSumoUser) so Meta dedupes them.
 */
export const createMetaEventID = () => uuidv4();

/** Same check as the backend (pickMetaTrackingCookies) and the RTDB rule. */
export const META_COOKIE_PATTERN = /^fb\.\d\.\d+\.[\w.-]{1,512}$/;

/**
 * _fbp/_fbc cookies (_fbc is also set from fbclid in index.html). Stored as
 * tracking.meta at signup and refreshed each session for server events.
 */
export const getMetaTrackingCookies = () => {
  const cookies = Object.fromEntries(
    document.cookie
      .split("; ")
      .filter(Boolean)
      .map((cookie) => {
        const index = cookie.indexOf("=");
        return [cookie.slice(0, index), cookie.slice(index + 1)];
      })
  );
  return { fbp: cookies._fbp, fbc: cookies._fbc };
};

export const pushDataLayer = (payload) => {
  if (!isProductionTracking || typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

//META
const pageViewMetaPixel = () => {
  if (!isProductionTracking || typeof window.fbq !== "function") return;
  window.fbq("track", "PageView");
};
const leadEventMetaPixel = (userData = {}, metaEventID) => {
  if (!isProductionTracking || typeof window.fbq !== "function") return;
  if (!metaEventID) return;
  const { email, phone, city, externalID } = userData;
  // Manual advanced matching: re-init sets the pixel's user data (hashed).
  const advancedMatching = {};
  if (email) advancedMatching.em = sha256LowerTrim(email);
  if (phone) advancedMatching.ph = sha256LowerTrim(normalizePhone(phone));
  if (city) advancedMatching.ct = sha256LowerTrim(normalizeCity(city));
  if (externalID) advancedMatching.external_id = sha256LowerTrim(externalID);
  if (Object.keys(advancedMatching).length > 0) {
    window.fbq("init", META_PIXEL_ID, advancedMatching);
  }
  window.fbq("track", "CompleteRegistration", {}, { eventID: metaEventID });
};
// const purchaseEventMetaPixel = (value, plan) => {
//   const payload = { currency: "USD" };
//   if (value != null && value !== "" && !Number.isNaN(Number(value))) {
//     payload.value = Number(value);
//   }
//   if (plan) {
//     payload.content_ids = [plan];
//     payload.content_type = "subscription";
//   }
//   window.fbq("track", "Purchase", payload);
// };

//GOOGLE ANALYTICS
const pageViewGoogleAnalyticsEvent = () => {
  pushDataLayer({
    event: "pageView",
  });
};
const leadGoogleAnalyticsEvent = (userData = {}) => {
  const { email, phone } = userData;
  const eventData = { event: "signup_completed", method: "email" };
  if (email || phone) {
    eventData.user_data = {};
    if (email) eventData.user_data.email = sha256LowerTrim(email);
    if (phone)
      eventData.user_data.phone_number = sha256LowerTrim(normalizePhone(phone));
  }
  pushDataLayer(eventData);
};
const purchaseGoogleAnalyticsEvent = (value, plan) => {
  const data = {
    event: "subscription_started",
    currency: "USD",
  };
  if (value != null && value !== "") data.value = value;
  if (plan) data.plan = plan;
  pushDataLayer(data);
};

//LINKEDIN INSIGHT TAG
// User data se envía en setUserDataInTracking (enhanced matching)
const leadLinkedInPixel = () => {
  if (!isProductionTracking || typeof window.lintrk !== "function") return;
  window.lintrk("track", { conversion_id: 25639202 });
};
const purchaseLinkedInPixel = (value) => {
  if (!isProductionTracking || typeof window.lintrk !== "function") return;
  window.lintrk("track", { conversion_id: 25639210, value: value ?? 0 });
};

export const trackingPageView = () => {
  if (!isProductionTracking) return;
  try {
    pageViewMetaPixel();
    pageViewGoogleAnalyticsEvent();
  } catch (error) {
    console.log("Error tracking page view", error);
  }
};

/**
 * Signup conversion. Meta CompleteRegistration fires only when metaEventID is
 * passed (a real new account, with the same id sent to the backend copy).
 */
export const trackingLead = (userData = {}, metaEventID) => {
  if (!isProductionTracking) return;
  try {
    leadEventMetaPixel(userData, metaEventID);
    leadGoogleAnalyticsEvent(userData);
    leadLinkedInPixel();
  } catch (error) {
    console.log("Error tracking lead", error);
  }
};

export const trackingPurchase = (value, plan) => {
  if (!isProductionTracking) return;
  try {
    // purchaseEventMetaPixel(value, plan); // Tracking in webhook backend
    purchaseGoogleAnalyticsEvent(value, plan);
    purchaseLinkedInPixel(value);
  } catch (error) {
    console.log("Error tracking purchase", error);
  }
};
